import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Timer, Users, PlayCircle, PauseCircle } from 'lucide-react';

// Four Choice Exercise Component
export function FourChoiceExercise({ exercise, onSave, savedResponse }) {
  const [responses, setResponses] = useState(savedResponse || {});

  const choices = [
    { id: 1, label: 'Hear blame, blame back', color: 'bg-red-50' },
    { id: 2, label: 'Hear blame, blame self', color: 'bg-orange-50' },
    { id: 3, label: 'Hear my own feelings & needs', color: 'bg-green-50' },
    { id: 4, label: "Hear speaker's feelings & needs", color: 'bg-blue-50' }
  ];

  useEffect(() => {
    onSave(responses);
  }, [responses]);

  return (
    <div className="space-y-8">
      {exercise.scenarios.map((scenario, idx) => (
        <Card key={idx} style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-xl">Scenario {idx + 1}: "{scenario}"</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {choices.map(choice => (
              <div key={choice.id} className={`p-4 ${choice.color}`}>
                <Label className="font-semibold mb-2 block">Choice {choice.id}: {choice.label}</Label>
                <Textarea
                  placeholder={`Your response...`}
                  value={responses[`${idx}-${choice.id}`] || ''}
                  onChange={(e) => setResponses({...responses, [`${idx}-${choice.id}`]: e.target.value})}
                  className="min-h-[80px] border-black bg-white"
                  style={{ borderRadius: 0 }}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Partner Exercise with Timer
export function PartnerTimedExercise({ exercise, onSave, savedResponse }) {
  const [timeLeft, setTimeLeft] = useState(exercise.time ? exercise.time * 60 : null);
  const [isRunning, setIsRunning] = useState(false);
  const [response, setResponse] = useState(savedResponse || '');

  useEffect(() => {
    let interval = null;
    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsRunning(false);
      alert('Time is up! Switch roles.');
    }
    return () => clearInterval(interval);
  }, [isRunning, timeLeft]);

  useEffect(() => {
    onSave(response);
  }, [response]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6" />
            <h4 className="text-xl font-bold">Partner Exercise</h4>
          </div>
          {exercise.time && (
            <div className="flex items-center gap-4">
              <div className="text-3xl font-bold">{formatTime(timeLeft)}</div>
              <Button
                onClick={() => setIsRunning(!isRunning)}
                variant="outline"
                className="border-black"
                style={{ borderRadius: 0 }}
              >
                {isRunning ? <PauseCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
              </Button>
              <Button
                onClick={() => setTimeLeft(exercise.time * 60)}
                variant="outline"
                className="border-black text-xs"
                style={{ borderRadius: 0 }}
              >
                Reset
              </Button>
            </div>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {exercise.partner_roles.map((role, i) => (
            <div key={i} className="p-3 bg-white border-2 border-black">
              <p className="font-semibold text-sm">{role}</p>
            </div>
          ))}
        </div>

        <p className="text-base italic text-gray-700">
          <strong>Remember:</strong> This is best done with a real person. Use Practice Solo mode below if you don't have a partner available.
        </p>
      </div>

      <div>
        <Label className="mb-2 block">Your Notes & Reflections</Label>
        <Textarea
          placeholder="Write about your experience with this partner exercise..."
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          className="min-h-[200px] border-black"
          style={{ borderRadius: 0 }}
        />
        <p className="text-sm text-gray-600 mt-2">✓ Auto-saved</p>
      </div>
    </div>
  );
}

// Translation Exercise
export function TranslationExercise({ exercise, onSave, savedResponse }) {
  const [translations, setTranslations] = useState(savedResponse || {});

  useEffect(() => {
    onSave(translations);
  }, [translations]);

  return (
    <div className="space-y-6">
      {exercise.items && exercise.items.map((item, idx) => (
        <div key={idx} className="p-6 border-2 border-gray-200 rounded-sm">
          <p className="text-lg font-semibold mb-4">{idx + 1}. "{item}"</p>
          <div className="space-y-3">
            <div>
              <Label>Translate to:</Label>
              <Input
                placeholder="I feel ___ because I need/value ___"
                value={translations[idx] || ''}
                onChange={(e) => setTranslations({...translations, [idx]: e.target.value})}
                className="border-black"
                style={{ borderRadius: 0 }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Observation vs Evaluation Exercise
export function ObsEvalExercise({ exercise, onSave, savedResponse }) {
  const [responses, setResponses] = useState(savedResponse || {});

  useEffect(() => {
    onSave(responses);
  }, [responses]);

  return (
    <div className="space-y-6">
      {exercise.items.map((item, idx) => (
        <div key={idx} className="p-6 border-2 border-gray-200 rounded-sm">
          <p className="text-lg mb-4">"{item}"</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Type:</Label>
              <div className="flex gap-4 mt-2">
                <button
                  onClick={() => setResponses({...responses, [`${idx}-type`]: 'O'})}
                  className={`px-6 py-2 border-2 font-semibold ${responses[`${idx}-type`] === 'O' ? 'bg-green-100 border-green-600' : 'border-gray-300'}`}
                >
                  Observation
                </button>
                <button
                  onClick={() => setResponses({...responses, [`${idx}-type`]: 'E'})}
                  className={`px-6 py-2 border-2 font-semibold ${responses[`${idx}-type`] === 'E' ? 'bg-red-100 border-red-600' : 'border-gray-300'}`}
                >
                  Evaluation
                </button>
              </div>
            </div>
            {responses[`${idx}-type`] === 'E' && (
              <div>
                <Label>Rewrite as pure observation:</Label>
                <Input
                  placeholder="What would a video camera record?"
                  value={responses[`${idx}-rewrite`] || ''}
                  onChange={(e) => setResponses({...responses, [`${idx}-rewrite`]: e.target.value})}
                  className="border-black mt-2"
                  style={{ borderRadius: 0 }}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Double OFNR (Honesty + Empathy)
export function DoubleOFNRExercise({ exercise, onSave, savedResponse }) {
  const [responses, setResponses] = useState(savedResponse || {});

  useEffect(() => {
    onSave(responses);
  }, [responses]);

  return (
    <div className="space-y-8">
      {exercise.scenarios.map((scenario, idx) => (
        <Card key={idx} style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-xl">"{scenario}"</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-green-50">
              <Label className="font-bold mb-2 block">Honesty OFNR (Express yourself):</Label>
              <Textarea
                placeholder="When... I feel... because I need... Would you be willing to...?"
                value={responses[`${idx}-honesty`] || ''}
                onChange={(e) => setResponses({...responses, [`${idx}-honesty`]: e.target.value})}
                className="min-h-[100px] border-black bg-white"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="p-4 bg-blue-50">
              <Label className="font-bold mb-2 block">Empathy OFNR (Guess their feelings/needs):</Label>
              <Textarea
                placeholder="When you... are you feeling... because you need...? Would you like...?"
                value={responses[`${idx}-empathy`] || ''}
                onChange={(e) => setResponses({...responses, [`${idx}-empathy`]: e.target.value})}
                className="min-h-[100px] border-black bg-white"
                style={{ borderRadius: 0 }}
              />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
